"use client";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API_ENDPOINTS } from "../../../constants/config";
import { ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";

function AddDepartment({ onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    try {
      setError(null);
      if (!name?.trim()) {
        setError("Department name is required");
        return;
      }
      await axiosInstance.post(API_ENDPOINTS.departments, { name: name.trim() });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add department");
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Add Department</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Department name"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Add Department</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditDepartment({ department, onClose, onSuccess }) {
  const [name, setName] = useState(department?.name || "");
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    try {
      setError(null);
      if (!name?.trim()) {
        setError("Department name is required");
        return;
      }
      await axiosInstance.patch(API_ENDPOINTS.department(department._id), { name: name.trim() });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update department");
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Department</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Department name"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Update</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function DepartmentList() {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const getSortIcon = (field) => {
    if (field !== sortField) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    return sortDirection === "asc" ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" /> : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />;
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSorted = () => {
    if (!sortField) return departments;
    return [...departments].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (sortField === "createdAt") {
        va = new Date(va).getTime();
        vb = new Date(vb).getTime();
      }
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDirection === "asc" ? -1 : 1;
      if (va > vb) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const fetchDepartments = useCallback(async () => {
    try {
      setError(null);
      const res = await axiosInstance.get(API_ENDPOINTS.departments);
      setDepartments(res.data?.data?.departments || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch departments");
    }
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      setError(null);
      await axiosInstance.delete(API_ENDPOINTS.department(id));
      fetchDepartments();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete department");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchDepartments}>Refresh List</Button>
            <Button variant="outline" onClick={() => navigate("/dashboard/settings/department-mapping")}>
              Teacher/Staff Mapping
            </Button>
          </div>
          <Button onClick={() => setShowAddModal(true)}>Add Department</Button>
        </div>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold text-gray-600 cursor-pointer" onClick={() => handleSort("name")}>
                <div className="flex items-center">Name {getSortIcon("name")}</div>
              </TableHead>
              <TableHead className="font-semibold text-gray-600 cursor-pointer" onClick={() => handleSort("createdAt")}>
                <div className="flex items-center">Created {getSortIcon("createdAt")}</div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getSorted().map((d) => (
              <TableRow key={d._id}>
                <TableCell>{d.name}</TableCell>
                <TableCell>{new Date(d.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setSelectedDepartment(d); setShowEditModal(true); }}>Edit</Button>
                    <Button variant="destructive" onClick={() => handleDelete(d._id)}>Delete</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {showAddModal && (
          <div className="mt-4">
            <AddDepartment
              onClose={() => setShowAddModal(false)}
              onSuccess={() => { setShowAddModal(false); fetchDepartments(); }}
            />
          </div>
        )}
        {showEditModal && selectedDepartment && (
          <div className="mt-4">
            <EditDepartment
              department={selectedDepartment}
              onClose={() => { setShowEditModal(false); setSelectedDepartment(null); }}
              onSuccess={() => { setShowEditModal(false); setSelectedDepartment(null); fetchDepartments(); }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
